import { EVENTS, type EventDef, type EventId } from './events.js';

/**
 * IAAF combined-events points. Track: a * (b - T)^c with T in seconds. Field:
 * a * (M - b)^c with M in centimetres for jumps and metres for throws. Points are
 * truncated, never rounded.
 */
export function pointsFor(ev: EventDef, mark: number | null): number {
  if (mark === null || !Number.isFinite(mark)) return 0;
  const { a, b, c, measure } = ev.scoring;
  const m = measure === 'centimetres' ? mark * 100 : mark;
  const base = measure === 'time' ? b - m : m - b;
  if (base <= 0) return 0;
  return Math.floor(a * base ** c);
}

export type Marks = Partial<Record<EventId, number | null>>;

export function totalPoints(marks: Marks): number {
  let total = 0;
  for (const ev of EVENTS) total += pointsFor(ev, marks[ev.id] ?? null);
  return total;
}

/** The mark needed for a given points score (inverse of pointsFor), for target displays. */
export function markForPoints(ev: EventDef, points: number): number {
  const { a, b, c, measure } = ev.scoring;
  const base = (points / a) ** (1 / c);
  const m = measure === 'time' ? b - base : b + base;
  return measure === 'centimetres' ? m / 100 : m;
}

/** F1-style league points per placing in a meet, plus a bonus for the best single event score. */
export const PLACE_POINTS = [25, 18, 15, 12, 10, 8];
export const BEST_EVENT_BONUS = 5;
export function pointsForPlace(place: number): number {
  return PLACE_POINTS[place - 1] ?? 0;
}
